import { useEffect, useRef } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    setTimeout(() => {
      el.style.transition = "opacity 1.2s ease, transform 1.2s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 100);
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative flex-1 min-h-screen flex items-center justify-center overflow-hidden bg-gray-950">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20dark%20professional%20business%20background%20with%20subtle%20geometric%20patterns%2C%20deep%20charcoal%20and%20black%20tones%2C%20minimalist%20luxury%20atmosphere%2C%20abstract%20lines%2C%20premium%20corporate%20aesthetic%2C%20very%20dark%20moody%20sophisticated&width=1920&height=1080&seq=home-bg-001&orientation=landscape"
            alt="Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
        </div>

        {/* Watermark Logo */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src="https://static.readdy.ai/image/2ff205c826c7478ce4cffa1fe381bc88/8b53dd28b3301c749b5023c4bbcf750b.png"
            alt=""
            className="w-[520px] h-[520px] object-contain opacity-[0.06] select-none"
          />
        </div>

        {/* Hero Content */}
        <div ref={heroRef} className="relative z-10 text-center px-6 w-full max-w-4xl mx-auto">
          {/* Logo visible */}
          <div className="flex justify-center mb-10">
            <img
              src="https://static.readdy.ai/image/2ff205c826c7478ce4cffa1fe381bc88/8b53dd28b3301c749b5023c4bbcf750b.png"
              alt="Stéphane Boutin"
              className="w-28 h-28 object-contain rounded-full border-2 border-amber-500/40"
            />
          </div>

          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-500 mb-6">
            stephane-boutin.com
          </p>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-white leading-tight mb-6 tracking-tight">
            Communiquer ses idées<span className="text-amber-500">…</span>
          </h1>

          <div className="w-16 h-0.5 bg-amber-500 mx-auto mb-8" />

          <p className="text-gray-300 text-base md:text-lg font-light tracking-wide max-w-xl mx-auto leading-relaxed">
            Développement d&apos;affaires · Performance · Logistique · Innovation
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="w-px h-10 bg-gradient-to-b from-amber-500/0 to-amber-500/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500/60" />
        </div>
      </section>

      {/* Tagline Section */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-500 mb-6">
            Notre approche
          </p>
          <h2 className="text-2xl md:text-3xl font-light text-gray-900 leading-relaxed mb-8">
            Une aide personnalisée et adaptée à vos projets d&apos;affaires
          </h2>
          <div className="w-12 h-0.5 bg-amber-500 mx-auto mb-8" />
          <p className="text-gray-500 text-base leading-relaxed max-w-2xl mx-auto">
            Analyse stratégique, solutions concrètes et accompagnement sur mesure pour propulser votre entreprise vers l&apos;excellence.
          </p>
        </div>
      </section>

      {/* Quick Services Preview */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-500 mb-12 text-center">
            Domaines d&apos;expertise
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-briefcase-line", title: "Développement d'affaires", desc: "Plans stratégiques, formation et financement" },
              { icon: "ri-mental-health-line", title: "Performance", desc: "Coaching, motivation et programmation mentale" },
              { icon: "ri-truck-line", title: "Logistique", desc: "Gestion d'articles et recherche d'acheteurs" },
              { icon: "ri-building-line", title: "Entretien ménager", desc: "Services sanitaires et entretien professionnel" },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-lg p-8 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-5">
                  <i className={`${item.icon} text-2xl text-amber-500`} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 leading-snug">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
